import { TILE_TYPES, ANIMATION_CONSTANTS, PHYSICS_CONSTANTS } from '@core/constants/index';
import { Position } from '@core/Position';

export interface NPCData {
    x: number;
    y: number;
    npcType?: string;
    id?: string;
    spriteKey?: string;
}

export class BaseNPC {
    private _position: Position;
    private _lastPosition: Position;

    public x: number;
    public y: number;
    public lastX: number;
    public lastY: number;

    public npcType: string;
    public id: string;
    public spriteKey: string;
    public bumpOffsetX: number;
    public bumpOffsetY: number;
    public bumpFrames: number;
    public liftOffsetY: number;
    public liftFrames: number;
    public isMoving: boolean;
    public movementPath: Position[];

    constructor(data: NPCData) {
        this._position = new Position(data.x, data.y);
        this._lastPosition = this._position.clone();

        Object.defineProperty(this, 'x', {
            get() { return this._position.x; },
            set(value: number) { this._position.x = value; }
        });
        Object.defineProperty(this, 'y', {
            get() { return this._position.y; },
            set(value: number) { this._position.y = value; }
        });
        Object.defineProperty(this, 'lastX', {
            get() { return this._lastPosition.x; },
            set(value: number) { this._lastPosition.x = value; }
        });
        Object.defineProperty(this, 'lastY', {
            get() { return this._lastPosition.y; },
            set(value: number) { this._lastPosition.y = value; }
        });

        this.npcType = data.npcType || 'generic';
        this.id = data.id || '';
        this.spriteKey = data.spriteKey || this.npcType;

        this.bumpOffsetX = 0;
        this.bumpOffsetY = 0;
        this.bumpFrames = 0;
        this.liftOffsetY = 0;
        this.liftFrames = 0;

        this.isMoving = false;
        this.movementPath = [];
    }

    setPosition(x: number, y: number): void {
        this._lastPosition = this._position.clone();
        this._position = new Position(x, y);
    }

    getPosition(): { x: number; y: number } {
        return this._position.toObject();
    }

    getPositionObject(): Position {
        return this._position;
    }

    startBump(deltaX: number, deltaY: number): void {
        this.bumpOffsetX = deltaX * PHYSICS_CONSTANTS.BUMP_OFFSET_MAGNITUDE;
        this.bumpOffsetY = deltaY * PHYSICS_CONSTANTS.BUMP_OFFSET_MAGNITUDE;
        this.bumpFrames = ANIMATION_CONSTANTS.BUMP_ANIMATION_FRAMES;
    }

    startLift(): void {
        this.liftFrames = ANIMATION_CONSTANTS.LIFT_FRAMES;
    }

    updateAnimations(): void {
        if (this.bumpFrames > 0) {
            this.bumpFrames--;
            this.bumpOffsetX *= PHYSICS_CONSTANTS.BUMP_DECAY_FACTOR;
            this.bumpOffsetY *= PHYSICS_CONSTANTS.BUMP_DECAY_FACTOR;
        }
        if (this.liftFrames > 0) {
            this.liftFrames--;
            const total = ANIMATION_CONSTANTS.LIFT_FRAMES;
            const elapsed = total - this.liftFrames;
            const t = Math.max(0, Math.min(1, elapsed / total));
            const maxLift = -28;
            this.liftOffsetY = maxLift * Math.sin(Math.PI * t);
        }
    }

    isWalkable(x: number, y: number, grid: any[][]): boolean {
        const pos = new Position(x, y);

        if (!pos.isInBounds(grid.length)) {
            return false;
        }

        const tile = pos.getTile(grid);
        const tileType = tile && tile.type ? tile.type : tile;

        const walkableTypes = [
            TILE_TYPES.FLOOR,
            TILE_TYPES.WATER,
            TILE_TYPES.FOOD,
            TILE_TYPES.AXE,
            TILE_TYPES.HAMMER,
            TILE_TYPES.BISHOP_SPEAR,
            TILE_TYPES.HORSE_ICON,
            TILE_TYPES.BOMB,
            TILE_TYPES.NOTE,
            TILE_TYPES.HEART,
            TILE_TYPES.PORT,
            TILE_TYPES.EXIT
        ];

        return walkableTypes.includes(tileType);
    }
}
